import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { BookOpen, ChevronRight, Volume2, MessageCircle } from 'lucide-react';

// Hikaye Modu Bileşeni - Senaryo içinde kelime öğretimi
export default function StoryMode({ title, story, vocabulary, onAnswer }) {
  const [currentPart, setCurrentPart] = useState(0);
  const [highlightedWords, setHighlightedWords] = useState([]);
  const [showVocab, setShowVocab] = useState(false);
  const [readParts, setReadParts] = useState([]);

  const parts = story.parts || [{ text: story.text, speaker: story.speaker }];
  const isLastPart = currentPart === parts.length - 1;
  const allRead = readParts.length === parts.length;

  // Kelime vurgulama
  const highlightText = (text) => {
    if (!vocabulary || vocabulary.length === 0) return text;
    
    let result = text;
    vocabulary.forEach(vocab => {
      const regex = new RegExp(`\\b(${vocab.word})\\b`, 'gi');
      result = result.replace(regex, `<span class="bg-yellow-200 px-1 rounded cursor-pointer hover:bg-yellow-300 font-semibold" data-word="${vocab.word}">$1</span>`);
    });
    return result;
  };

  const handleNext = () => {
    if (!readParts.includes(currentPart)) {
      setReadParts([...readParts, currentPart]);
    }
    
    if (isLastPart) {
      if (showVocab || !vocabulary || vocabulary.length === 0) {
        if (onAnswer) onAnswer(true, 1);
      } else {
        setShowVocab(true);
      }
    } else {
      setCurrentPart(prev => prev + 1);
    }
  };

  const speakText = (text) => {
    if ('speechSynthesis' in window) {
      // HTML taglerini temizle
      const cleanText = text.replace(/<[^>]*>/g, '');
      const utterance = new SpeechSynthesisUtterance(cleanText);
      utterance.lang = 'en-US';
      utterance.rate = 0.85;
      window.speechSynthesis.speak(utterance);
    }
  };

  const currentStoryPart = parts[currentPart];

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-orange-100 rounded-xl">
          <BookOpen className="w-6 h-6 text-orange-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-800">{title}</h2>
          <p className="text-sm text-gray-500">Hikaye Modu</p>
        </div>
      </div>

      {/* Progress */}
      <div className="flex gap-1 mb-6">
        {parts.map((_, idx) => (
          <div 
            key={idx}
            className={`flex-1 h-2 rounded-full transition-colors ${
              readParts.includes(idx) ? 'bg-orange-500' : 
              idx === currentPart ? 'bg-orange-300' : 'bg-gray-200'
            }`}
          />
        ))}
      </div>

      {!showVocab ? (
        /* Story Card */
        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden mb-6">
          {/* Scene Image/Emoji */}
          {(currentStoryPart.image || currentStoryPart.emoji) && (
            <div className="h-32 bg-gradient-to-br from-orange-50 to-yellow-50 flex items-center justify-center">
              {currentStoryPart.image ? (
                <img src={currentStoryPart.image} alt="scene" className="max-h-28 object-contain" />
              ) : (
                <span className="text-6xl">{currentStoryPart.emoji}</span>
              )}
            </div>
          )}

          {/* Speaker & Text */}
          <div className="p-6">
            {currentStoryPart.speaker && (
              <div className="flex items-center gap-2 mb-3">
                <MessageCircle className="w-4 h-4 text-blue-500" />
                <span className="text-sm font-bold text-blue-600">{currentStoryPart.speaker}</span>
              </div>
            )}
            
            <div className="relative">
              <div 
                className="text-lg text-gray-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: highlightText(currentStoryPart.text) }}
                onClick={(e) => {
                  if (e.target.dataset.word) {
                    const word = vocabulary.find(v => v.word.toLowerCase() === e.target.dataset.word.toLowerCase());
                    if (word) {
                      alert(`${word.word}: ${word.translation}`);
                    }
                  }
                }}
              />
              
              <button
                onClick={() => speakText(currentStoryPart.text)}
                className="absolute -right-2 -top-2 p-2 bg-blue-100 hover:bg-blue-200 rounded-full transition-colors"
              >
                <Volume2 className="w-5 h-5 text-blue-600" />
              </button>
            </div>

            {currentStoryPart.translation && (
              <p className="mt-4 text-gray-500 italic border-t pt-4">
                {currentStoryPart.translation}
              </p>
            )}
          </div>
        </div>
      ) : (
        /* Vocabulary Summary */
        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-6 mb-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">📚 Öğrendiğin Kelimeler</h3>
          <div className="space-y-3">
            {vocabulary.map((vocab, idx) => (
              <div key={idx} className="flex items-center justify-between bg-gray-50 rounded-xl p-3">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{vocab.emoji || '📝'}</span>
                  <div>
                    <p className="font-bold text-gray-800">{vocab.word}</p>
                    <p className="text-sm text-gray-500">{vocab.translation}</p>
                  </div>
                </div>
                <button
                  onClick={() => speakText(vocab.word)}
                  className="p-2 hover:bg-gray-200 rounded-full"
                >
                  <Volume2 className="w-4 h-4 text-blue-500" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 mb-6">
        <p className="text-sm text-yellow-800">
          💡 <strong>İpucu:</strong> Sarı ile işaretli kelimelere tıklayarak anlamlarını görebilirsin!
        </p>
      </div>

      {/* Continue Button */}
      <Button
        onClick={handleNext}
        className="w-full py-4 text-lg font-bold bg-orange-500 hover:bg-orange-600 rounded-xl"
      >
        {showVocab ? 'Tamamla' : isLastPart ? 'Kelimeleri Gözden Geçir' : 'Devam Et'}
        <ChevronRight className="w-5 h-5 ml-2" />
      </Button>
    </div>
  );
}
